import get from "lodash/get";
import { ComponentConfig, WidgetConfig } from "../Widget/Widget";

export const getCloseBounds = (
  componentBounds: { [index: string]: number },
  widgetConfig: WidgetConfig,
  configName: string
) => {
  const relativeBounds = createRelativeBoundsMap(widgetConfig, configName);
  removeRelativeBounds();

  return relativeBounds.find((relativeBound) => {
    const closeToLeft =
      Math.abs(relativeBound.left - componentBounds.left) < 10 ||
      Math.abs(relativeBound.left - componentBounds.right) < 10;
    const closeToRight =
      Math.abs(relativeBound.right - componentBounds.left) < 10 ||
      Math.abs(relativeBound.right - componentBounds.right) < 10;
    const closeToTop =
      Math.abs(relativeBound.top - componentBounds.top) < 10 ||
      Math.abs(relativeBound.top - componentBounds.bottom) < 10;
    const closeToBottom =
      Math.abs(relativeBound.bottom - componentBounds.top) < 10 ||
      Math.abs(relativeBound.bottom - componentBounds.bottom) < 10;

    createRelativeBounds({
      closeToBottom,
      closeToLeft,
      closeToRight,
      closeToTop,
      relativeBound,
    });

    return closeToLeft || closeToRight || closeToTop || closeToBottom;
  });
};

const createRelativeBounds = ({
  closeToBottom,
  closeToLeft,
  closeToRight,
  closeToTop,
  relativeBound,
}: {
  closeToBottom: boolean;
  closeToLeft: boolean;
  closeToRight: boolean;
  closeToTop: boolean;
  relativeBound: {
    left: number;
    top: number;
    right: number;
    bottom: number;
  };
}) => {
  if (closeToLeft) {
    const verticalGuideLine = document.createElement("div");
    verticalGuideLine.classList.add(
      "component-base-vertical-relative-guide-line"
    );
    verticalGuideLine.id = "relative-bound-vertical-left";
    verticalGuideLine.style.height = "100%";
    verticalGuideLine.style.left = relativeBound.left + "px";
    document.body.appendChild(verticalGuideLine);
  }

  if (closeToBottom) {
    const horizontalGuideLine = document.createElement("div");
    horizontalGuideLine.classList.add(
      "component-base-horizontal-relative-guide-line"
    );
    horizontalGuideLine.id = "relative-bound-horizontal-bottom";
    horizontalGuideLine.style.width = "100%";
    horizontalGuideLine.style.top = relativeBound.bottom + "px";
    document.body.appendChild(horizontalGuideLine);
  }

  if (closeToRight) {
    const horizontalGuideLine = document.createElement("div");
    horizontalGuideLine.classList.add(
      "component-base-horizontal-relative-guide-line"
    );
    horizontalGuideLine.id = "relative-bound-vertical-right";
    horizontalGuideLine.style.width = "100%";
    horizontalGuideLine.style.top = relativeBound.right + "px";
    document.body.appendChild(horizontalGuideLine);
  }

  if (closeToTop) {
    const horizontalGuideLine = document.createElement("div");
    horizontalGuideLine.classList.add(
      "component-base-horizontal-relative-guide-line"
    );
    horizontalGuideLine.id = "relative-bound-horizontal-top";
    horizontalGuideLine.style.width = "100%";
    horizontalGuideLine.style.top = relativeBound.top + "px";
    document.body.appendChild(horizontalGuideLine);
  }
};

export const removeRelativeBounds = () => {
  removeRelativeBound("relative-bound-vertical-left");
  removeRelativeBound("relative-bound-horizontal-top");
  removeRelativeBound("relative-bound-vertical-right");
  removeRelativeBound("relative-bound-horizontal-bottom");
};

const removeRelativeBound = (relativeBoundId: string) => {
  const relativeBound = document.getElementById(relativeBoundId);

  if (relativeBound && document.body.contains(relativeBound)) {
    document.body.removeChild(relativeBound);
  }
};

export const createRelativeBoundsMap = (
  widgetConfig: WidgetConfig,
  configName: string
) => {
  const parentScreen = findParentScreen(
    widgetConfig,
    configName
  ) as ComponentConfig;
  if (parentScreen) {
    const screenChildren = getScreenChildren(widgetConfig, parentScreen);
    const bounds = screenChildren
      .filter(([componentId, componentConfig]) => componentId !== configName)
      .map(([componentId, componentConfig]) =>
        getComponentBounds(componentConfig as ComponentConfig)
      )
      .filter((componentBound) => !Object.values(componentBound).includes(0));

    return bounds;
  }

  return [];
};

const getScreens = (widgetConfig: WidgetConfig) =>
  Object.values(widgetConfig).filter(
    (componentConfig: ComponentConfig) => componentConfig.component === "Screen"
  );

const findParentScreen = (widgetConfig: WidgetConfig, configName: string) => {
  const screens = getScreens(widgetConfig);

  return screens.find(
    (screen: ComponentConfig) =>
      screen.children && screen.children.includes(configName)
  );
};

const componentsToFilterOut = ["FriendbuyLogo", "TermsAndConditions"];
const getScreenChildren = (
  widgetConfig: WidgetConfig,
  screen: ComponentConfig
) =>
  Object.entries(widgetConfig).filter(
    ([componentId, componentConfig]) =>
      screen.children &&
      screen.children.includes(componentId) &&
      !componentsToFilterOut.includes(
        (componentConfig as ComponentConfig).component
      )
  );

const getComponentBounds = (component: ComponentConfig) => ({
  left: getBoundNumber(get(component, "style.left")),
  top: getBoundNumber(get(component, "style.top")),
  right:
    getBoundNumber(get(component, "style.left")) +
    getBoundNumber(get(component, "style.width")),
  bottom:
    getBoundNumber(get(component, "style.top")) +
    getBoundNumber(get(component, "style.height")),
});

const getBoundNumber = (value: string) => {
  if (!value || value.includes("%")) {
    return 0;
  }

  return Number((value || "0").replace(/(px)/g, ""));
};
